import { useEffect, useState } from 'react';
import { useCiAuthStore } from '../auth/ciAuthStore';
import { useCiPoolStore } from '../authorization/ciPoolStore';
import { fetchTopPlayers, isAuthorizationError } from '../data/ciApi';
import type { TopPlayer, TopPlayerMetric, TimeWindow } from '../data/types';
import { ROSTER_MOVES_TRACKED_SINCE, TIME_WINDOW_DAYS, TIME_WINDOW_LABELS } from '../data/types';
import { CiTimeWindowTabs } from '../components/CiTimeWindowTabs';
import { RosterMovesNotice } from '../components/RosterMovesNotice';

const METRICS: { id: TopPlayerMetric; label: string }[] = [
  { id: 'signed', label: 'Plus signés' },
  { id: 'dropped', label: 'Plus libérés' },
  { id: 'injured', label: 'Plus placés en LTIR' },
  { id: 'traded', label: 'Plus échangés' },
];

const LIMIT = 15;

interface Section {
  metric: TopPlayerMetric;
  label: string;
  rows: TopPlayer[];
  loading: boolean;
  error: string | null;
}

export function PlayersPage() {
  const userId = useCiAuthStore((s) => s.user_id);
  const leagueId = useCiPoolStore((s) => s.selectedLeagueId);
  const [window, setWindow] = useState<TimeWindow>('30d');
  const [sections, setSections] = useState<Section[]>(
    METRICS.map((m) => ({ metric: m.id, label: m.label, rows: [], loading: true, error: null }))
  );

  useEffect(() => {
    let cancelled = false;
    if (!userId || !leagueId) return;
    setSections((prev) => prev.map((s) => ({ ...s, loading: true, error: null })));
    const days = TIME_WINDOW_DAYS[window];
    (async () => {
      const results = await Promise.all(
        METRICS.map((m) => fetchTopPlayers(userId, leagueId, m.id, days, LIMIT))
      );
      if (cancelled) return;
      setSections(
        METRICS.map((m, i) => {
          const r = results[i];
          if (r.error) {
            return {
              metric: m.id,
              label: m.label,
              rows: [],
              loading: false,
              error: isAuthorizationError(r.error)
                ? 'Accès refusé pour ce pool.'
                : 'Erreur de chargement.',
            };
          }
          return {
            metric: m.id,
            label: m.label,
            rows: r.data ?? [],
            loading: false,
            error: null,
          };
        })
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, leagueId, window]);

  if (!leagueId) {
    return <div className="ci-empty">Sélectionnez un pool pour afficher les joueurs.</div>;
  }

  return (
    <div>
      <section className="ci-section">
        <div className="ci-section__header">
          <h2 className="ci-section__title">Joueurs les plus actifs</h2>
          <CiTimeWindowTabs value={window} onChange={setWindow} />
        </div>
        <p className="ci-section__meta">
          Fenêtre : {TIME_WINDOW_LABELS[window].toLowerCase()} — top {LIMIT} par catégorie.
        </p>
      </section>

      <RosterMovesNotice />

      <div className="ci-grid-3">
        {sections.map((section) => (
          <section key={section.metric} className="ci-section">
            <div className="ci-section__header">
              <h3 className="ci-section__title ci-section__title--sm">{section.label}</h3>
            </div>
            {(section.metric === 'dropped' || section.metric === 'injured') && (
              <p className="ci-section__meta">
                <strong>Depuis le {ROSTER_MOVES_TRACKED_SINCE} seulement.</strong>
              </p>
            )}
            {section.loading ? (
              <div className="ci-empty">Chargement…</div>
            ) : section.error ? (
              <div className="ci-error">{section.error}</div>
            ) : section.rows.length === 0 ? (
              <div className="ci-empty">Aucun évènement sur cette fenêtre.</div>
            ) : (
              <table className="ci-table" data-testid={`ci-top-${section.metric}`}>
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>#</th>
                    <th>Joueur</th>
                    <th style={{ width: 64, textAlign: 'right' }}>N</th>
                  </tr>
                </thead>
                <tbody>
                  {section.rows.map((row, idx) => (
                    <tr key={`${row.player_id}-${idx}`}>
                      <td>{idx + 1}</td>
                      <td>{row.player_name || `Joueur #${row.player_id}`}</td>
                      <td style={{ textAlign: 'right' }}>{row.event_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
